import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import api from "../utils/api/axiosInstance";
import { PUBLIC_PAGES } from "../config/publicPages";

// All pages enabled by default (fail-open while loading or on error)
const defaultPages = Object.fromEntries(
  Object.entries(PUBLIC_PAGES).map(([key, page]) => [key, page.defaultEnabled])
);

const PublicPagesContext = createContext({
  pages: defaultPages,
  isPageEnabled: () => true,
  loading: true,
  // Fail open while loading/on error — never lock visitors out of a site
  // that is actually reachable just because this one request failed.
  maintenanceMode: false,
});

export const PublicPagesProvider = ({ children }) => {
  const [pages, setPages] = useState(defaultPages);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchPages = useCallback(async () => {
    try {
      const res = await api.get("/settings/public");
      if (res.data?.publicPages) {
        setPages({ ...defaultPages, ...res.data.publicPages });
      }
      if (typeof res.data?.maintenanceMode === "boolean") {
        setMaintenanceMode(res.data.maintenanceMode);
      }
    } catch {
      // Fail open — leave pages/maintenance as the defaults (site usable)
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  // Listen for admin saves to refetch immediately without a page reload
  useEffect(() => {
    const handleChange = () => fetchPages();
    window.addEventListener("public-pages-changed", handleChange);
    return () => window.removeEventListener("public-pages-changed", handleChange);
  }, [fetchPages]);

  const isPageEnabled = useCallback(
    (key) => pages[key] !== false,
    [pages]
  );

  return (
    <PublicPagesContext.Provider
      value={{ pages, isPageEnabled, loading, maintenanceMode }}
    >
      {children}
    </PublicPagesContext.Provider>
  );
};

export const usePublicPages = () => useContext(PublicPagesContext);
